import moment from "moment";
import { Regexes } from "./Regexes";

export const BankAccountMax = 4;

export const Routes = Object.freeze({
  DEPOSIT: 'addFunds',
  WITHDRAW: 'withdrawFunds',
  DASHBOARD: 'dashboard',
  MY_ACCOUNTS: 'myAccounts',
  ADD_ACCOUNT: 'addAccount'
});

export const EnrollmentStatus = Object.freeze({
  NOT_ENROLLED: 0,
})

export const DateFormat = 'MM/DD/YYYY';
export const ExpirationDateFormat = 'MMYY';

export const ValidationMessages = Object.freeze({
  REQUIRED: "Required.",
  GENERAL: {
    FIRST_NAME: "First Name is required.",
    LAST_NAME: "Last Name is required.",
    MIDDLE_NAME: "No special characters.",
    EMAIL: "Email is required and is not the right format.",
    DOB: `"Date of Birth is required, in the format ${DateFormat} and must meet state's age requirements."`,
    PHONE: "Phone Number is required and must be in the format ###-###-####. Cannot all be the same number.",
  },
  ID: {
    NUMBER: "ID Number is required and must be at least 6 characters.",
    STATE: "ID State is required.",
    EXPIRATION: "Expiration is required, must be valid, and in the format MM/YY.",
    SSN: "SSN is required and must be 9 numbers in the format ###-##-####.",
  },
  ADDRESS: {
    ADDRESS: "Address is required.",
    CITY: "City is required.",
    STATE: "State is required.",
    ZIP: "Zip is required.",
  },
  BANK: {
    ACCOUNT_NUMBER: "Account Number is required and must be at least 4 numbers.",
    ROUTING_NUMBER: "Routing Number is required and must be at least 8 numbers.",
  },
})

export const Validators = Object.freeze({
  NAME_LENGTH: {
     errorMessage: 'Cannot be longer than 20 characters.',
     regex: Regexes.TWENTY_MAX
  },
  NAME_CHARS: {
    errorMessage: 'No special characters.',
    regex: Regexes.NAME
  },
  EMAIL: {
    errorMessage: "Invalid email format.",
    regex: Regexes.EMAIL
  },
  AGE_18: {
    errorMessage: "Must meet state's age requirements.",
    action: (date) => date <= moment().subtract(18, 'years').toDate()
  },
  PHONE_FORMAT: {
    errorMessage: "Must be in the format ###-###-####.",
    regex: Regexes.FORMATTED_PHONE
  },
  PHONE_NOT_ALL_SAME: {
    errorMessage: 'Cannot all be the same number. Ex: 999-999-9999.',
    action: (phone) => {
      const noDashes = phone.replace(/-/g,'');
      if (noDashes.length === 10)
      {
        return !Regexes.PHONE_ALL_MATCH.test(noDashes);
      }
      else
      {
        // Don't check this rule until all numbers have been entered
        return true;
      }
    }
  },
  CC_FORMAT: {
    errorMessage: "Must be in the format ####-####-####-####.",
    regex: Regexes.FORMATTED_CC
  },
  ADDRESS: {
    errorMessage: 'Invalid format.',
    regex: Regexes.ADDRESS.FORMATTED
  },
  HOUSE_NUMBER: {
    errorMessage: 'Invalid format.',
    regex: Regexes.HOUSE_NUMBER.FORMATTED
  },
  UNIT: {
    errorMessage: 'Invalid format.',
    regex: Regexes.UNIT.FORMATTED
  },
  CITY: {
    errorMessage: 'Invalid format.',
    regex: Regexes.CITY.FORMATTED
  },
  ZIP: {
    errorMessage: 'Must be 5 digit, 9 digit, or 5 digit dash 4 digit (#####-####). ',
    regex: Regexes.ZIP.FORMATTED_US
  },
  ID_NUMBER_MIN_LENGTH: {
    errorMessage: 'Must be between 6 and 30 letters or digits.',
    regex: Regexes.ID_LENGTH
  },
  ID_NUMBER_FORMAT: {
    errorMessage: 'No special characters or spaces.',
    regex: Regexes.FORMATTED_ID_NUMBER
  },
  ID_EXPIRATION_FORMAT: {
    errorMessage: 'Must be MMYY, 2 digit month followed by 2 digit year.',
    regex: Regexes.ID_EXPIRES
  },
  ID_EXPIRATION_DATE: {
    errorMessage: 'ID has expired or date is invalid.',
    action: (text) => {
      const match = text.match(/^(\d{2})(\d{2})$/);
      if (!match || match.length !== 3) {
        // Couldn't parse
        return false;
      }
      const year = "20" + match[2]; // Update this in 80 years!
      const month = match[1] - 1; // IMPORTANT JS Date is 0 based for month...

      var expDate = new Date(year, month + 1); // But allow exp date if it matches this month.

      return expDate > new Date(); // Today
    }
  },
  SSN: {
    errorMessage: 'Must be 9 digits.',
    regex: Regexes.SSN
  },
  NUMBERS_ONLY: {
    errorMessage: 'Numbers only.',
    regex: Regexes.NUMBER
  },
  BANK_ACCOUNT_LENGTH: {
    errorMessage: 'Must be between 4 and 17 digits.',
    regex: /^.{4,17}$/
  },
  ROUTING_NUMBER_LENGTH: {
    errorMessage: 'Must be between 8 and 9 digits.',
    regex: /^.{8,9}$/
  }
})

export const ENVS = Object.freeze({
  DEVELOPMENT: "development",
  TEST: "test",
  PRODUCTION: "production",
});

export const EnrollmentSteps = Object.freeze({
  GENERAL: {
    FIRST_NAME: "firstName",
    MI: "middleInitial",
    LAST_NAME: "lastName",
    EMAIL: "email",
    DOB: "dateOfBirth",
    PHONE: "mobilePhone",
  },
  ID: {
    TYPE: "idType",
    NUMBER: "idNumber",
    STATE: "idState",
    EXPIRATION: "idExpiration",
    SSN: "ssn",
    COUNTRY: "country",
  },
  ADDRESS: {
    ADDRESS: "address",
    UNIT: "unit",
    CITY: "city",
    STATE: "state",
    ZIP: "zip",
  },
  BANK: {
    ACCOUNT_NUMBER: "accountNumber",
    ROUTING_NUMBER: "routingNumber",
  },
});

export const fromOperatorDefault = {
    // general: [
    //   EnrollmentSteps.GENERAL.FIRST_NAME,
    //   EnrollmentSteps.GENERAL.MI,
    //   EnrollmentSteps.GENERAL.LAST_NAME,
    //   EnrollmentSteps.GENERAL.EMAIL,
    //   EnrollmentSteps.GENERAL.DOB,
    //   EnrollmentSteps.GENERAL.PHONE,
    // ],
    id: [
      EnrollmentSteps.ID.TYPE,
      EnrollmentSteps.ID.NUMBER,
      EnrollmentSteps.ID.STATE,
      EnrollmentSteps.ID.EXPIRATION,
      EnrollmentSteps.ID.SSN,
      EnrollmentSteps.ID.COUNTRY,
    ],
    // address: [
    //   EnrollmentSteps.ADDRESS.ADDRESS,
    //   EnrollmentSteps.ADDRESS.UNIT,
    //   EnrollmentSteps.ADDRESS.CITY,
    //   EnrollmentSteps.ADDRESS.STATE,
    //   EnrollmentSteps.ADDRESS.ZIP,
    // ],
    bank: [
      EnrollmentSteps.BANK.ACCOUNT_NUMBER,
      EnrollmentSteps.BANK.ROUTING_NUMBER,
    ],
};

export const formDataDefault = {
  firstName: "",
  middleInitial: "",
  lastName: "",
  email: "",
  dateOfBirth: "",
  mobilePhone: "",
  idType: "",
  idNumber: "",
  idState: "",
  idExpiration: "",
  ssn: "",
  streetAddress: "",
  // houseNumber: "",
  unit: "",
  city: "",
  state: "",
  zip: "",
  country: "USA",
  accountNumber: "",
  routingNumber: "",
  plaidData: null,
  netspendData: null,
};

export const ID_TYPES = Object.freeze({
  DRIVERS_LICENSE: "DL",
  STATE_ID: "ST",
  MILITARY_ID: "MI",
  PASSPORT: "PP",
  // TRIBAL_ID: "TI",
});

export const ID_TYPES_OPTIONS = Object.freeze([
  {
    value: '',
    label: 'Choose one'
  },
  {
    value: ID_TYPES.DRIVERS_LICENSE,
    label: 'Driver\'s License',
  },
  {
    value: ID_TYPES.STATE_ID,
    label: 'State ID',
  },
  {
    value: ID_TYPES.MILITARY_ID,
    label: 'Military ID',
  },
  {
    value: ID_TYPES.PASSPORT,
    label: 'Passport',
  },
  // {
  //   value: ID_TYPES.TRIBAL_ID,
  //   label: 'Tribal ID',
  // },
]);

export const COUNTRY_OPTIONS = Object.freeze([
  {
    value: "2",
    label: "USA",
  }
]);

export const STATE_OPTIONS = Object.freeze([
  {
    value: "",
    label: "--",
  },{
    value: "AL",
    label: "AL",
  },
  {
    value: "AK",
    label: "AK",
  },
  {
    value: "AR",
    label: "AR",
  },
  {
    value: "AZ",
    label: "AZ",
  },
  {
    value: "CA",
    label: "CA",
  },
  {
    value: "CO",
    label: "CO",
  },
  {
    value: "CT",
    label: "CT",
  },
  {
    value: "DE",
    label: "DE",
  },
  {
    value: "FL",
    label: "FL",
  },
  {
    value: "GA",
    label: "GA",
  },
  {
    value: "HI",
    label: "HI",
  },
  {
    value: "IA",
    label: "IA",
  },
  {
    value: "ID",
    label: "ID",
  },
  {
    value: "IL",
    label: "IL",
  },
  {
    value: "IN",
    label: "IN",
  },
  {
    value: "KS",
    label: "KS",
  },
  {
    value: "KY",
    label: "KY",
  },
  {
    value: "LA",
    label: "LA",
  },
  {
    value: "MA",
    label: "MA",
  },
  {
    value: "MD",
    label: "MD",
  },
  {
    value: "ME",
    label: "ME",
  },
  {
    value: "MI",
    label: "MI",
  },
  {
    value: "MN",
    label: "MN",
  },
  {
    value: "MO",
    label: "MO",
  },
  {
    value: "MS",
    label: "MS",
  },
  {
    value: "MT",
    label: "MT",
  },
  {
    value: "NC",
    label: "NC",
  },
  {
    value: "NE",
    label: "NE",
  },
  {
    value: "NH",
    label: "NH",
  },
  {
    value: "NJ",
    label: "NJ",
  },
  {
    value: "NM",
    label: "NM",
  },
  {
    value: "NV",
    label: "NV",
  },
  {
    value: "NY",
    label: "NY",
  },
  {
    value: "ND",
    label: "ND",
  },
  {
    value: "OH",
    label: "OH",
  },
  {
    value: "OK",
    label: "OK",
  },
  {
    value: "OR",
    label: "OR",
  },
  {
    value: "PA",
    label: "PA",
  },
  {
    value: "RI",
    label: "RI",
  },
  {
    value: "SC",
    label: "SC",
  },
  {
    value: "SD",
    label: "SD",
  },
  {
    value: "TN",
    label: "TN",
  },
  {
    value: "TX",
    label: "TX",
  },
  {
    value: "UT",
    label: "UT",
  },
  {
    value: "VT",
    label: "VT",
  },
  {
    value: "VA",
    label: "VA",
  },
  {
    value: "WA",
    label: "WA",
  },
  {
    value: "WI",
    label: "WI",
  },
  {
    value: "WV",
    label: "WV",
  },
  {
    value: "WY",
    label: "WY",
  },{
    value: "DC",
    label: "DC",
  },
  {
    value: "AS",
    label: "AS",
  },
  {
    value: "GU",
    label: "GU",
  },
  {
    value: "MP",
    label: "MP",
  },
  {
    value: "PR",
    label: "PR",
  },
  {
    value: "UM",
    label: "UM",
  },{
    value: "VI",
    label: "VI",
  },
]);

// export const TMXBASEADDRESS = 'https://h.online-metrix.net/fp/tags.js';
export const IPADDRESSURL = 'https://ifconfig.me/all.json'; // 'https://api.ipify.org/?format=json';  // https://ipapi.co/json/

export const PRODUCTTYPES = Object.freeze({
  ONLINE: "Online",
  PREFERRED: "Preferred",
  CREDITCARD: "CardAcceptance"
});

export const FUND_SOURCE_TYPE = Object.freeze({
  None: "None",
  MANUAL: "Manual",
  PLAID: "Plaid",
});

export const GPURLPATH = "/gaming/mobility/sdk";
export const PAVILIONURLPATH = "/sdk";

export const GLBANOTICELINK = "https://vippreferred.com/glba/";